import {
  Box,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TablePagination,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { blue, green, grey, orange, pink, purple } from '@material-ui/core/colors';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AB_TEST_AUDIENCE_TYPE, AB_TEST_TYPE, IABTest, IABTestListOption, IUserSegment } from '../../services/types';
import { RouteNames } from '../config';
import useStyles from './styles';

interface IProps {
  abTests: IABTest[];
  totalCount: number;
  listOption: IABTestListOption;
  onRequestFetch: (page: number, rowsPerPage: number) => void;
  onClickRow: (testId: number | null) => void;
  onChangeListOption: (listOption: IABTestListOption) => void;
}

const audienceTypeToDisplay = (audienceType: AB_TEST_AUDIENCE_TYPE | undefined) => {
  if (audienceType === AB_TEST_AUDIENCE_TYPE.SEGMENT) {
    return 'SEGMENT';
  } else if (audienceType === AB_TEST_AUDIENCE_TYPE.SEGMENT_NO_CONTROL) {
    return 'SEGMENT(No Control)';
  } else {
    return audienceType || '';
  }
};

const segmentToDisplay = (segment: IUserSegment) => {
  return;
};

const ABTestList = (props: IProps): JSX.Element => {
  const { abTests, totalCount, onRequestFetch, onChangeListOption, listOption } = props;
  const classes = useStyles();

  const [rowsPerPage, setRowsPerPage] = useState<number>(20);
  const [page, setPage] = useState<number>(0);

  const changeIsUsable = (activeOnly: string | undefined) => {
    onChangeListOption({ activeOnly, testType: listOption.testType });
  };
  const changeTestType = (testType: AB_TEST_TYPE | '') => {
    onChangeListOption({ activeOnly: listOption.activeOnly, testType: testType });
  };

  const colorByTestType = (testType: string | undefined) => {
    switch (testType) {
      case 'EPISODE':
        return orange[500];
      case 'DISPLAY':
        return blue[500];
      case 'BUNDLE':
        return pink[500];
      case 'SEASON':
        return green[500];
      case 'STORE':
        return purple[500];
      default:
        return grey[500];
    }
  };

  return (
    <Box>
      <Box display="flex" flexDirection="row" alignItems="center" paddingBottom={2}>
        <RadioGroup
          row
          value={listOption.activeOnly ? 'true' : ''}
          name="isUsable"
          onChange={(e, v) => {
            changeIsUsable(v);
          }}
          style={{ flex: 1 }}
        >
          <FormControlLabel control={<Radio value="" color="primary" />} label="All Tests" />
          <FormControlLabel control={<Radio value="true" color="primary" />} label="Active Test Only" />
        </RadioGroup>
        <Select
          value={listOption.testType || 'all'}
          onChange={(e, c) => {
            changeTestType(e.target.value as AB_TEST_TYPE);
          }}
          className={classes.testTypeSelect}
        >
          <MenuItem key={'testype-item-00'} value={'all'}>
            All Types
          </MenuItem>
          {['PERFORMANCE', 'DISPLAY', 'EPISODE', 'BUNDLE'].map((v, i) => (
            <MenuItem key={`slot-${i}`} value={v}>
              {v}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Paper>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell align="center" style={{ width: 80 }}>
                ID
              </TableCell>
              <TableCell align="center" style={{ width: 100 }}>
                Type
              </TableCell>
              <TableCell align="center">Title</TableCell>
              <TableCell align="center" padding="none">
                Target
              </TableCell>
              <TableCell align="center" style={{ width: 100 }}>
                Status
              </TableCell>
              <TableCell align="center" style={{ width: 100 }}>
                Audience
                <br />
                Type
              </TableCell>
              <TableCell align="center" style={{ width: 100 }}>
                Audience
                <br />
                Value
              </TableCell>
              <TableCell align="center" style={{ width: 125 }}>
                Test Period
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {abTests &&
              abTests.map((item, idx) => (
                <TableRow
                  key={item.id}
                  component={Link}
                  to={`${RouteNames.Abtest}/v/${item.id}`}
                  onClick={() => {
                    props.onClickRow(item.id);
                  }}
                >
                  <TableCell align="center">{item.id}</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={item.testType}
                      className={classes.frontChip}
                      style={{ backgroundColor: colorByTestType(item.testType), color: '#fff' }}
                    />
                  </TableCell>
                  <TableCell align="left">
                    <span style={{ marginRight: 5 }}>{item.testName}</span>
                  </TableCell>
                  <TableCell align="left">
                    <Box display="flex" justifyContent="center" flexWrap="nowrap">
                      <Chip size="small" label={item.testTargetType} className={classes.testTargetType} variant="outlined" />
                      <Chip size="small" label={item.testTargetValue} className={classes.testTargetValue} />
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {!item.isUsable && 'Deleted'}
                    {item.isUsable && moment().isBefore(item.startDate) && 'Scheduled'}
                    {item.isUsable && moment().isAfter(item.endDate) && 'Expired'}
                    {item.isUsable && moment().isAfter(item.startDate) && moment().isBefore(item.endDate) && 'Active'}
                  </TableCell>
                  <TableCell align="left">{audienceTypeToDisplay(item.audienceType)}</TableCell>
                  <TableCell align="left">
                    {item.audienceType === 'RANDOM' && <div>{Intl.NumberFormat().format(item.audienceValue || 0)} % EXPOSED</div>}
                    {item.audienceType === 'CONDITIONAL' && (item.audienceValue === 0 ? 'NEW USER' : 'OLD USER')}
                    {item.segmentId && item.segment && (
                      <Tooltip title={item.segment.name}>
                        <div className={classes.segmentColumn}>{item.segment.name}</div>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="left">
                    {moment(item.startDate).format('ll')} ~ <br />
                    {moment(item.endDate).format('ll')}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        {totalCount !== undefined && (
          <TablePagination
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={(e: unknown, v: number) => {
              onRequestFetch(v, rowsPerPage);
              setPage(v);
            }}
            onChangeRowsPerPage={(event: React.ChangeEvent<HTMLInputElement>) => {
              const rpp = parseInt(event.target.value, 10);
              setRowsPerPage(rpp);
              onRequestFetch(0, rpp);
              setPage(0);
            }}
          />
        )}
      </Paper>
    </Box>
  );
};

export default ABTestList;
